"use client";

import { useLingui } from "@lingui/react/macro";
import { AccessibleIcon } from "@offstreetparking/design-system/accessibility";
import { formatDate } from "@offstreetparking/design-system/formatters";
import { DSCopyButton } from "@offstreetparking/design-system/forms";
import { PermitIcon } from "@offstreetparking/design-system/icons/PermitIcon";
import { Text } from "@offstreetparking/design-system/typography";
import { classNames } from "@offstreetparking/design-system/utils";
import { type IntervalSchedule, applyScheduleToDateTime } from "@offstreetparking/domain/value/interval";
import { PermitStatus } from "@offstreetparking/domain/value/permits";
import { useEffect } from "react";
import { useKiosk } from "../../../providers/KioskProvider";
import type { Permit } from "../types";
import { preloadQRCodeChunk } from "./LazyQRCodeSVG";
import { ParcsCodePrintLayout } from "./ParcsCodePrintLayout";
import { ParcsCodeQRCarousel } from "./ParcsCodeQRCarousel";

type PermitStatusHeaderProps = {
	parcsCodes: Permit["parcsCodes"];
	initialQrIndex: number;
	publicIdentifier: Permit["publicIdentifier"];
	earliestStartDateTimeISO?: string;
	latestEndDateTimeISO?: string | null;
	earliestStartSchedules?: IntervalSchedule[];
	latestEndSchedules?: IntervalSchedule[];
	timezone: string;
	permitStatus: PermitStatus;
	updateDate: Permit["updateDate"];
	isPaymentPending?: boolean;
};
export const PermitStatusHeader = ({
	parcsCodes,
	initialQrIndex,
	publicIdentifier,
	earliestStartDateTimeISO,
	latestEndDateTimeISO,
	earliestStartSchedules,
	latestEndSchedules,
	timezone,
	permitStatus,
	updateDate,
	isPaymentPending = false,
}: PermitStatusHeaderProps) => {
	const { isKiosk } = useKiosk();
	const { t } = useLingui();
	const isPermitInactive =
		permitStatus === PermitStatus.EXPIRED || permitStatus === PermitStatus.VOID || isPaymentPending;
	const hasParcsCodes = parcsCodes.length > 0 && !isPaymentPending;

	useEffect(() => {
		if (hasParcsCodes) {
			void preloadQRCodeChunk();
		}
	}, [hasParcsCodes]);

	const getPermitStatusText = () => {
		switch (permitStatus) {
			case PermitStatus.VOID:
				return t`Canceled on ${formatDate({
					date: updateDate,
					formatType: "Abbreviated_Date",
					timezone,
				})} at ${formatDate({
					date: updateDate,
					formatType: "Abbreviated_Time",
					timezone,
				})}`;
			case PermitStatus.UPCOMING:
				if (!earliestStartDateTimeISO) {
					return null;
				}
				return t`Starts ${formatDate({
					date: earliestStartDateTimeISO,
					formatType: "Abbreviated_Date",
					timezone,
				})} at ${formatDate({
					date: applyScheduleToDateTime({
						dateTimeISO: earliestStartDateTimeISO,
						timezone,
						schedules: earliestStartSchedules,
						boundary: "start",
					}),
					formatType: "Abbreviated_Time",
					timezone,
				})}`;
			case PermitStatus.ACTIVE:
				if (!latestEndDateTimeISO) {
					return null;
				}
				return t`Expires on ${formatDate({
					date: latestEndDateTimeISO,
					formatType: "Abbreviated_Date",
					timezone,
				})} at ${formatDate({
					date: applyScheduleToDateTime({
						dateTimeISO: latestEndDateTimeISO,
						timezone,
						schedules: latestEndSchedules,
						boundary: "end",
					}),
					formatType: "Abbreviated_Time",
					timezone,
				})}`;
			case PermitStatus.EXPIRED:
				if (!latestEndDateTimeISO) {
					return null;
				}
				return t`Expired on ${formatDate({
					date: latestEndDateTimeISO,
					formatType: "Abbreviated_Date",
					timezone,
				})} at ${formatDate({
					date: applyScheduleToDateTime({
						dateTimeISO: latestEndDateTimeISO,
						timezone,
						schedules: latestEndSchedules,
						boundary: "end",
					}),
					formatType: "Abbreviated_Time",
					timezone,
				})}`;
			default:
				return null;
		}
	};
	return (
		<>
			{hasParcsCodes && (
				<>
					<div className="flex flex-col items-center justify-center gap-4 md:flex-row print:hidden">
						<div className="flex flex-1 items-center justify-center">
							<ParcsCodeQRCarousel parcsCodes={parcsCodes} initialQrIndex={initialQrIndex} />
						</div>
					</div>
					<ParcsCodePrintLayout parcsCodes={parcsCodes} publicIdentifier={publicIdentifier} />
				</>
			)}
			<div className="rounded-lg border border-neutral-faded">
				<div className="flex min-h-20">
					<div
						className={classNames(
							"relative flex w-20 items-center justify-center rounded-tl-lg rounded-bl-lg [print-color-adjust:exact]",
							isPermitInactive ? "bg-disabled" : "bg-primary",
						)}
					>
						<AccessibleIcon>
							<PermitIcon
								className={classNames(
									"size-10",
									isPermitInactive ? "text-disabled" : "text-on-primary",
								)}
							/>
						</AccessibleIcon>
						<div className="absolute inset-y-0 right-0 flex translate-x-1/3 flex-col justify-between print:hidden">
							{[1, 2, 3, 4].map((key) => (
								<div
									key={`notch-${key}`}
									className={classNames(
										"h-4 w-2 rounded-md",
										isPermitInactive ? "bg-disabled" : "bg-primary",
									)}
								/>
							))}
						</div>
					</div>
					<div className="flex flex-1 items-center justify-between px-4">
						<div>
							<Text size="sm" weight="medium">
								{t`Your parking permit`}
							</Text>
							<Text size="2xl" weight="bold">
								{t`#${publicIdentifier.toUpperCase()}`}
							</Text>
						</div>
						{!isKiosk && (
							<div className="flex size-16 flex-col items-center justify-center rounded-full bg-neutral-faded print:hidden">
								<DSCopyButton
									size="md"
									i18n={{
										copied: t`Copied!`,
										copyToClipboard: t`Copy to clipboard`,
									}}
									text={publicIdentifier}
								/>
								<Text size="xs" weight="medium">
									{t`Copy`}
								</Text>
							</div>
						)}
					</div>
				</div>
			</div>
			{isPaymentPending ? (
				<div className="text-center">
					<Text size="md" weight="medium">
						{t`Payment not completed`}
					</Text>
				</div>
			) : (
				getPermitStatusText() && (
					<div className="text-center">
						<Text size="md" weight="medium">
							{getPermitStatusText()}
						</Text>
					</div>
				)
			)}
		</>
	);
};
